// Implementation example can be seen in src/components/filters/Views/Views.tsx
import { useCallback, useEffect, useRef, useState } from 'react';

export function useHorizontalOverflow() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
  const [hasOverflowRight, setHasOverflowRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const hasHorizontalOverflow = scroller.scrollWidth > scroller.clientWidth;
    const currentScrollLeft = scroller.scrollLeft;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

    setHasOverflowLeft(hasHorizontalOverflow && currentScrollLeft > 5);
    setHasOverflowRight(
      hasHorizontalOverflow && currentScrollLeft < maxScrollLeft - 5,
    );
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const timeoutId = setTimeout(checkOverflow, 100);

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(scroller);

    // MutationObserver covers childList changes (views added/removed) and
    // characterData changes (label renames), so no external length dep needed.
    const mutationObserver = new MutationObserver(checkOverflow);
    mutationObserver.observe(scroller, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const handleScroll = () => checkOverflow();
    scroller.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      scroller.removeEventListener('scroll', handleScroll);
    };
  }, [checkOverflow]);

  const scrollLeft = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: -(scroller.clientWidth * 0.7),
      behavior: 'smooth',
    });
  }, []);

  const scrollRight = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({ left: scroller.clientWidth * 0.7, behavior: 'smooth' });
  }, []);

  return {
    scrollerRef,
    hasOverflowLeft,
    hasOverflowRight,
    scrollLeft,
    scrollRight,
  };
}
